<script lang="ts">
import { computed, defineComponent, ref } from "vue";
import formatUtils from "@/businesslogic/utils/formatUtils";
import IconSprite from "@/components/IconSprite.vue";
import Logger from "js-logger";
import dataUtils from "@/businesslogic/utils/dataUtils";
import { useFormStore } from "@/businesslogic/stores/formStore";

export default defineComponent({
  name: "LeistungstabelleZeile",
  components: { IconSprite },
  props: {
    zeilen: {
      type: Array,
      required: true,
    },
    aktiveLinie: {
      type: Number,
      required: true,
    },
  },
  setup(props) {
    const piniaStore = useFormStore();
    const zeilenInhalt = ref(props.zeilen);
    const { formatCurrency } = formatUtils();
    const { gte, lte } = dataUtils();

    const sichtbareZeilen = computed(() => {
      return zeilenInhalt.value.filter((item) => istSichtbar(item));
    });

    function getCssForCol(col: number, aktiveLine: number): string {
      if (col === aktiveLine) {
        return "tariff-column-" + col + " active" + " js-tarifoptions-table-column-" + col;
      }
      return "tariff-column-" + col + " js-tarifoptions-table-column-" + col;
    }

    function zeigeInfo(zeile: any) {
      zeile.show = !zeile.show;
    }

    function getBerechneLeistung(wert: string): string {
      const versicherungssumme = piniaStore.state.tarifinformationen.versicherungssumme;
      let berechneteLeistung = "";
      if (wert.indexOf("%") && versicherungssumme !== null) {
        const leistungTemp = wert.split("%");
        berechneteLeistung =
          formatCurrency(Math.round((parseInt(leistungTemp[0]) / 100) * versicherungssumme), 0) + leistungTemp[1];
      }
      return berechneteLeistung;
    }

    function photovoltaikUnterZehnJahreAlt(): boolean {
      const date = new Date();
      const vorZehnJahren = date.getFullYear() - 10;
      return gte(piniaStore.state.gebaeudedaten.photovoltaikBaujahr, vorZehnJahren);
    }

    function zeigPhotovoltaikErtragsausfall(): boolean {
      Logger.trace(
        "zeigPhotovoltaikErtragsausfall: ",
        "photovoltaikVorhanden:",
        piniaStore.state.gebaeudedaten.photovoltaikVorhanden,
        "gebaeudetyp:",
        piniaStore.state.gebaeudedaten.gebaeudetyp,
        "photovoltaikLeistung:",
        piniaStore.state.gebaeudedaten.photovoltaikLeistung,
      );
      const einfamilienhaus = "8";
      const mehrfamilienhaus = "3";
      if (piniaStore.state.gebaeudedaten.photovoltaikVorhanden === true) {
        if (
          piniaStore.state.gebaeudedaten.gebaeudetyp === einfamilienhaus &&
          lte(piniaStore.state.gebaeudedaten.photovoltaikLeistung, 15)
        ) {
          return photovoltaikUnterZehnJahreAlt();
        }
        // Mehrfamilienhaus
        if (
          piniaStore.state.gebaeudedaten.gebaeudetyp === mehrfamilienhaus &&
          lte(piniaStore.state.gebaeudedaten.photovoltaikLeistung, 25)
        ) {
          return photovoltaikUnterZehnJahreAlt();
        }
      }

      return false;
    }

    function istSichtbar(zeile: any): boolean {
      if (zeile.photovoltaikErtragsausfallPruefen) {
        return zeigPhotovoltaikErtragsausfall();
      }
      return true;
    }

    return {
      zeigeInfo,
      getCssForCol,
      getBerechneLeistung,
      istSichtbar,
      sichtbareZeilen,
    };
  },
});
</script>

<template>
  <div v-for="(zeile, index) in sichtbareZeilen" :key="zeile.index">
    <div
      class="o-grid table-row gev19__contexthelp"
      :class="{
        'first-row': index === 0,
        'last-row': index === sichtbareZeilen.length - 1,
      }"
    >
      <div class="o-grid__cell o-grid__cell--12 o-grid__cell--3-480 u-text-align-right--480 tariff-left-col">
        {{ zeile.leistungstext }}
        <span
          v-if="zeile.infoText.length"
          class="gev19__contexthelp-open"
          title="Hilfe anzeigen"
          @click="zeigeInfo(zeile)"
        ></span>
      </div>
      <div
        class="o-grid__cell o-grid__cell--12 o-grid__cell--9-480 o-grid__cell--8-768 o-grid__cell--7-1024 o-grid__cell--6-1200 tariff-right-col"
      >
        <div class="o-grid o-grid--no-y-gutter add-on tarif-options-module">
          <div
            v-for="(leistung, index) in zeile.leistungen"
            :key="index"
            class="add-on__option"
            v-bind:class="getCssForCol(index + 1, aktiveLinie)"
          >
            <div v-if="leistung === 'Ja'" class="u-text-align-center vertikal-zentrieren u-color-secondary">
              <icon-sprite icon-name="icon-check-mark" />
            </div>
            <div v-else-if="leistung === 'Nein'" class="u-text-align-center vertikal-zentrieren">
              <div class="">
                <icon-sprite icon-name="icon-minus" />
              </div>
            </div>
            <div v-else-if="zeile.berechnung === true" class="u-text-align-center vertikal-zentrieren">
              <div class="">{{ getBerechneLeistung(leistung) }}</div>
            </div>
            <div v-else class="u-text-align-center vertikal-zentrieren">
              <div :class="zeile.leistungsstil">
                {{ leistung }}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div
        class="o-grid__cell o-grid__cell--12 o-grid__cell--12-480 o-grid__cell--11-768 o-grid__cell--10-1024 o-grid__cell--9-1200"
        style="border-bottom: 1px solid #000000 !important"
      >
        <div v-if="zeile.show" class="gev19__contexthelp-text gev19__contexthelp-inlinetabletest">
          <div
            v-if="zeile.show"
            @click="zeigeInfo(zeile)"
            class="gev19__contexthelp-close"
            title="Hilfe schließen"
          ></div>
          <div v-html="zeile.infoText"></div>
        </div>
      </div>
    </div>
    <div class="o-grid gimmick">
      <div class="o-grid__cell o-grid__cell--12 o-grid__cell--3-480 gimmick-left-col"></div>
      <div
        class="o-grid__cell o-grid__cell--12 o-grid__cell--9-480 o-grid__cell--8-768 o-grid__cell--7-1024 o-grid__cell--6-1200 gimmick-right-col"
      >
        <div class="o-grid o-grid--no-y-gutter add-on">
          <div class="tariff-column-1 add-on__option"></div>
          <div class="tariff-column-2 add-on__option"></div>
          <div class="tariff-column-3 add-on__option"></div>
        </div>
      </div>
    </div>
  </div>
  <div class="o-grid gimmick gimmick__bottom">
    <div class="o-grid__cell o-grid__cell--12 o-grid__cell--3-480 gimmick-left-col"></div>
    <div
      class="o-grid__cell o-grid__cell--12 o-grid__cell--9-480 o-grid__cell--8-768 o-grid__cell--7-1024 o-grid__cell--6-1200 gimmick-right-col"
    >
      <div class="o-grid o-grid--no-y-gutter add-on">
        <div class="tariff-column-1 add-on__option"></div>
        <div class="tariff-column-2 add-on__option"></div>
        <div class="tariff-column-3 add-on__option"></div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss"></style>
